<template>
    <select ref="selectElement" class="form-control">
        <option v-for="option in enumOptions" :key="option.value" :value="option.value">
            {{ i18n(`${props.enumTranslationNamespace}.${option.name}`) }}
        </option>
    </select>
</template>

<script setup lang="ts">
    import { type EnumValueNamePair, Misc } from "@/data/util/Misc";
    import { i18n } from "@/portal/Trans";
    import { Select } from "@/portal/formControls/Select";
    import { onMounted, ref, watch } from "vue";

    const selectedValue = defineModel<number | undefined>({
        required: true,
    });

    const props = defineProps<{
        enumDataSource: any;
        enumTranslationNamespace: string;
        optionsFilter?: (value: number) => boolean;
        disabled?: boolean;
        defaultValue?: number;
        showAllText?: string;
    }>();

    const selectElement = ref<HTMLElement | null>(null);
    let select: Select<object>;

    const enumOptions = ref<EnumValueNamePair[]>(Misc.enumsToValueNameArray(props.enumDataSource));
    if (props.optionsFilter) {
        enumOptions.value = enumOptions.value.filter((option) => props.optionsFilter!(option.value));
    }

    const internalSelectValue = ref<string | undefined>();
    onMounted(() => {
        if (!props.enumDataSource)
            throw new Error("No Select data source provided! Please provide either dataSource object or enum!");

        if (props.enumDataSource && !props.enumTranslationNamespace)
            throw new Error("Translation namespace is required! e.g. 'enums.CancellationReason'");

        select = Select.createFromSelectWithOptions<object>({
            $select: $(selectElement.value!),
            showAllText: props.showAllText,
        });

        if (props.disabled) {
            select.setDisabled(true);
        }

        if (selectedValue.value != null && select.getOptions().includes(selectedValue.value.toString())) {
            select.setValue(selectedValue.value);
        } else if (props.defaultValue) {
            select.setValue(props.defaultValue.toString());
        }
        internalSelectValue.value = select.getValue();
        select.onChange = () => {
            internalSelectValue.value = select.getValue();
        };
    });

    watch(internalSelectValue, (newValue) => {
        selectedValue.value = newValue ? parseInt(newValue) : undefined;
    });

    watch(selectedValue, (newValue) => {
        if (newValue !== undefined) {
            select.setValue(newValue);
        }
    });

    watch(
        () => props.disabled,
        (disabledValue) => {
            if (disabledValue) {
                select.setDisabled(true);
            } else {
                select.setDisabled(false);
            }
        }
    );
</script>
